import { openForm } from '../utils/openform';
import { updateReferrerFields } from '../utils/updatereferrerfields';
import { closeAllForms } from '../utils/closeallforms';
import { getCSRFToken } from '../utils/getCSRFToken';
import { postJSONWithCSRFRetry } from '../utils/fetchWithCSRFRetry';
import { formatDateISO, parseDate } from '../utils/dateUtils';
import { safeJsonParse } from '../utils/authErrorHandler';

export default function registerAddBlockForm() {
  window.Alpine.store('addBlockForm', {
    // Store variables
    calendarId: '',
    unitId: '',
    tourDepartureId: '',
    selectedAccommodationUnitId: null,
    selectedTourUnitId: null,
    startDate: '',
    endDate: '',
    product: '',
    units: 1,
    totalUnits: 1,
    carriedUnitIds: [],
    carriedStart: '',
    carriedEnd: '',
    carriedUnits: null,
    suppressDateReset: false,
    flatpickrInstances: {},
    unavailableDates: [], // format should be ['23-01-2024']
    categorizedDates: {
      start: [],
      single: [],
      middle: [],
      end: [],
    },

    // ADD NEW BLOCK ITEM (button on add new booking form)
    async handleAddNewBlock(e) {
      console.log('ADD BLOCK FORM CLICKED!!!');
      const form = e.target.closest('form');
      const booking = Alpine.store('addBookingForm');
      const formType = form.querySelector('.form_type_field').value;
      if (formType == 'accommodation') {
        this.product = 'Accommodation';
      } else {
        this.product = 'Tour';
      }
      this.calendarId = form.getAttribute('data-calendar-id');

      const picked =
        booking.flatpickrInstances?.[booking.product]?.selectedDates || [];
      const startDate =
        booking.startDate ||
        (picked[0] ? formatDateISO(picked[0]) : '') ||
        booking.prefilledStart ||
        form.querySelector('.start-date-field')?.value ||
        '';
      const endDate =
        booking.endDate ||
        (picked[1] ? formatDateISO(picked[1]) : '') ||
        booking.prefilledEnd ||
        form.querySelector('[name="end_date"]')?.value ||
        '';
      const unitIdSelect = form.querySelector(
        '.selectedUnit .form-control.slideForm-field',
      );
      let unitId = unitIdSelect?.value || booking.unitId || '';
      const pinnedUnitIds = (booking.pinnedUnitIds || [])
        .map(String)
        .filter(Boolean);
      if (!unitId && pinnedUnitIds.length) {
        unitId = pinnedUnitIds[0];
      }
      if (!unitId && this.product === 'Tour') {
        const productId = booking.productId;
        const tour = Alpine.store('calendarStore').tours.find(
          (item) => item.id === productId,
        );
        unitId = tour?.tour_units?.[0]?.id;
      }
      const tourDepartureId =
        form.querySelector('[name="tour_departure_id"]')?.value ||
        booking.tourDepartureId ||
        '';

      this.carriedStart = startDate;
      this.carriedEnd = endDate;
      this.startDate = startDate;
      this.endDate = endDate;
      this.units =
        pinnedUnitIds.length > 1
          ? pinnedUnitIds.length
          : Math.max(1, Number(booking.units) || 1);
      this.carriedUnits = this.units;
      this.carriedUnitIds = pinnedUnitIds.length > 1 ? pinnedUnitIds : [];
      this.unitId = unitId;
      this.tourDepartureId = tourDepartureId;
      if (this.product == 'Accommodation') {
        this.selectedAccommodationUnitId = unitId;
      } else {
        this.selectedTourUnitId = unitId;
      }

      closeAllForms();
      await this.renderForm();
      console.log('product:', this.product);

      this.applyCarriedDates();

      // Get form to be used
      let formIdToOpen;

      if (this.product == 'Accommodation') {
        formIdToOpen = 'addAccommodationBlockForm';
        // Update the URL to include the selected unit for filtering
        this.selectedAccommodationUnitId = unitId;
      } else {
        formIdToOpen = 'addTourBlockForm';
        // Update the URL to include the selected unit for filtering
        this.selectedTourUnitId = unitId;
      }

      const fieldData = {
        start_date: startDate,
        end_date: endDate,
        amount_units: this.units,
        accommodation_unit_id: unitId,
        tour_unit_id: unitId,
        tour_departure_id: tourDepartureId,
      };

      openForm(formIdToOpen, 'open', fieldData);
      document.body.classList.add('slideform-open'); // This is to prevent the background from scrolling when the form is open
      this.updateInitialDateForIdentifier(this.product, startDate);
      updateReferrerFields();

      // Update amount_units dropdown after form is opened and unit field is set
      // Add a small delay to ensure form is fully rendered
      setTimeout(() => {
        this.applyCarriedDates();
        this.updateAmountUnitsForSelectedUnit(unitId);
      }, 100);
    },
    updateAmountUnitsForSelectedUnit(unitId) {
      // Find the specific form for this product type
      const formId =
        this.product === 'Accommodation'
          ? 'addAccommodationBlockForm'
          : 'addTourBlockForm';
      const amountUnitsSelect = document.querySelector(
        `#${formId} select[name="amount_units"]`,
      );

      if (unitId && amountUnitsSelect) {
        // Make an AJAX call to get the total units for the selected product
        this.fetchAvailableUnitsCount(unitId, amountUnitsSelect);
      }
    },
    async fetchAvailableUnitsCount(unitId, amountUnitsSelect) {
      try {
        const response = await fetch('/calendar/get-unit-count/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            unit_id: unitId,
            product_type: this.product,
          }),
        });

        const data = await safeJsonParse(response);
        if (data.total_units) {
          const keep = Math.min(
            Math.max(Number(this.carriedUnits || this.units) || 1, 1),
            data.total_units,
          );
          this.suppressDateReset = true;
          try {
            amountUnitsSelect.innerHTML = '';
            for (let i = 1; i <= data.total_units; i++) {
              const option = document.createElement('option');
              option.value = i;
              option.textContent = i;
              amountUnitsSelect.appendChild(option);
            }
            this.units = keep;
            amountUnitsSelect.value = String(keep);
          } finally {
            this.suppressDateReset = false;
          }
          this.applyCarriedDates();
        }
      } catch (error) {
        console.error('Error fetching available units count:', error);
      }
    },
    handleUnitChange() {
      console.log('FUNCTION CALLED: handleUnitChange - Block Form');
      console.log('Units changed to:', this.units);
      if (this.suppressDateReset) return;
      if (
        this.carriedStart &&
        this.carriedEnd &&
        Number(this.units) === Number(this.carriedUnits)
      ) {
        this.applyCarriedDates();
        return;
      }
      this.carriedStart = '';
      this.carriedEnd = '';
      this.carriedUnits = null;
      if (
        this.carriedUnitIds.length &&
        Number(this.units) !== this.carriedUnitIds.length
      ) {
        this.carriedUnitIds = [];
      }

      // Reset dates when units change to force re-validation
      this.startDate = '';
      this.endDate = '';

      // Clear and reset Flatpickr if it exists
      if (this.flatpickrInstances[this.product]) {
        this.flatpickrInstances[this.product].clear();
        this.flatpickrInstances[this.product].set('disable', [
          function () {
            return true; // Disable all dates initially
          },
        ]);

        // Re-fetch availability with new unit count
        const instance = this.flatpickrInstances[this.product];
        const currentYear = instance.currentYear;
        const currentMonth = instance.currentMonth + 1;
        this.fetchUnavailableDates(
          currentYear.toString(),
          currentMonth.toString(),
          instance,
        );
      }
    },

    async renderForm() {
      // Render the form on the DOM
      let formContainer;
      let addBlockForm;
      let url;

      if (this.product === 'Accommodation') {
        formContainer = document.getElementById(
          'add-accommodation-block-form-container',
        );
        addBlockForm = document.getElementById('addAccommodationBlockForm');
        const unitParam = this.selectedAccommodationUnitId
          ? `?unit_id=${this.selectedAccommodationUnitId}`
          : '';
        url = `/calendar/add-accommodation-block-form/${this.calendarId}${unitParam}`;
      } else if (this.product === 'Tour') {
        formContainer = document.getElementById(
          'add-tour-block-form-container',
        );
        addBlockForm = document.getElementById('addTourBlockForm');
        const unitParam = this.selectedTourUnitId
          ? `?unit_id=${this.selectedTourUnitId}`
          : '';
        url = `/calendar/add-tour-block-form/${this.calendarId}${unitParam}`;
      }

      if (addBlockForm) {
        // Form is already rendered, just open it
        return; // Exit the function early
      }

      // Fetch the product form from the server
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-CSRFToken': getCSRFToken(),
        },
      });

      // Use safe JSON parsing to handle auth errors gracefully
      const data = await safeJsonParse(response);
      // Update the form container with the fetched HTML
      formContainer.innerHTML = data.add_block_form_html;
    },
    initializeFlatpickr(product, flatpickrElement) {
      this.flatpickrInstances[product] = flatpickr(flatpickrElement, {
        mode: 'range',
        dateFormat: 'd/m/Y',
        showMonths: 2, // Range picker always shows two months

        onChange: (selectedDates) => {
          this.startDate = selectedDates[0]
            ? formatDateISO(selectedDates[0]) // Timezone-safe YYYY-MM-DD format
            : '';
          this.endDate = selectedDates[1]
            ? formatDateISO(selectedDates[1])
            : '';
          // Manually call updatePrice after updating the dates
          //this.product = product;
          this.updateFlatpickrDates(product);
        },
        onOpen: (selectedDates, dateStr, instance) => {
          this.product = product;

          // Disable all dates when initially opened
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1; // +1 because getMonth() is zero-indexed
          // Fetch unavailable dates for the currently viewed month and year when the picker opens
          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
          );
        },
        onMonthChange: (selectedDates, dateStr, instance) => {
          // Disable all dates when month changes
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1; // +1 because getMonth() is zero-indexed

          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
          );
        },
        onYearChange: (selectedDates, dateStr, instance) => {
          // Disable all dates when month changes
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1; // Adjusted to be 1-indexed

          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
          );
        },
      });
      this.applyCarriedDates();
    },
    applyCarriedDates() {
      const instance = this.flatpickrInstances[this.product];
      const startValue = this.carriedStart || this.startDate;
      const endValue = this.carriedEnd || this.endDate;
      if (!instance || !startValue || !endValue) return;
      const start = parseDate(startValue);
      const end = parseDate(endValue);
      if (!start || !end) return;
      this.startDate = startValue;
      this.endDate = endValue;
      instance.set('disable', []);
      instance.setDate([start, end], false);
      if (instance.selectedDates.length < 2) {
        instance.selectedDates = [start, end];
        instance.latestSelectedDateObj = end;
        instance.input.value = `${instance.formatDate(
          start,
          'd/m/Y',
        )} to ${instance.formatDate(end, 'd/m/Y')}`;
      }
      instance.jumpToDate(start, false);
    },
    updateFlatpickrDates(product) {
      const instance = this.flatpickrInstances[product];

      if (instance && instance.daysContainer) {
        // Disable middle dates to avoid user adding ranges that span them
        // STILL NEED TO FIGURE OUT HOW TO HANDLE SINGLE NIGHT BOOKINGS!!!
        instance.set('disable', this.unavailableDates);
        // Convert categorized_dates arrays into Set for efficient lookup
        console.log('categorizedDates:', this.categorizedDates);
        this.categorizedDates = {
          start: new Set(this.categorizedDates.start),
          single: new Set(this.categorizedDates.single),
          middle: new Set(this.categorizedDates.middle),
          end: new Set(this.categorizedDates.end),
        };
        // Get all day elements inside the Flatpickr calendar
        const dayElements =
          instance.daysContainer.querySelectorAll('.flatpickr-day');
        // Loop through all day elements
        dayElements.forEach((dayElem) => {
          // Get the date string for the day element and convert it
          const ariaLabel = dayElem.getAttribute('aria-label');
          const dateStr = this.convertAriaLabelToDateStr(ariaLabel); // Convert to DD-MM-YYYY format

          // Apply custom styling based on categorized dates
          if (this.categorizedDates.start.has(dateStr)) {
            dayElem.classList.add('start-date');
          }
          if (this.categorizedDates.single.has(dateStr)) {
            dayElem.classList.add('single-date');
          }
          if (this.categorizedDates.middle.has(dateStr)) {
            dayElem.classList.add('middle-date');
          }
          if (this.categorizedDates.end.has(dateStr)) {
            dayElem.classList.add('end-date');
          }
        });
      } else {
        console.error(`Flatpickr instance for '${product}' not found.`);
      }
    },
    fetchUnavailableDates(year, month, instance) {
      const csrfToken = getCSRFToken();

      // Block forms always use 2-month mode (range picker shows two months)
      let requestData = {
        year: year,
        month: month,
        unitId: this.unitId,
        tourDepartureId: this.tourDepartureId,
        amountUnits: this.units,
        two_month_mode: true,
      };
      // Disable date selection until availability has been fetched and updated
      this.disableFlatpickr(instance, true);

      postJSONWithCSRFRetry('/bookings_app/get-unavailable-dates/', requestData)
        .then(async (response) => {
          const data = await safeJsonParse(response);
          return data;
        })
        .then((data) => {
          this.categorizedDates = data.categorized_dates;
          this.unavailableDates = data.categorized_dates.middle;

          this.updateFlatpickrDates(this.product);
          // Re-enable date selection now that availability has been updated
          this.disableFlatpickr(instance, false);
        })
        .catch((error) => {
          console.error('Error fetching unavailable dates:', error);
        });
    },
    disableFlatpickr(flatpickrInstance, disable) {
      // Disable or enable the input field
      flatpickrInstance.input.disabled = disable;

      // Disable or enable the prev/next navigation buttons, if they exist
      if (flatpickrInstance.prevMonthNav && flatpickrInstance.nextMonthNav) {
        flatpickrInstance.prevMonthNav.disabled = disable;
        flatpickrInstance.nextMonthNav.disabled = disable;
      }

      // Optionally, disable the year and month dropdowns if present
      const pluginElements =
        flatpickrInstance.calendarContainer.querySelectorAll(
          '.flatpickr-monthDropdown-months, .numInput',
        );
      pluginElements.forEach((element) => {
        element.disabled = disable;
      });
    },
    updateInitialDateForIdentifier(product, isoDate) {
      if (this.flatpickrInstances[product]) {
        const newDate = parseDate(isoDate);
        if (newDate) {
          this.flatpickrInstances[product].jumpToDate(newDate, false);
        }
      }
    },
    convertAriaLabelToDateStr(ariaLabel) {
      const date = new Date(ariaLabel);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() is zero-indexed
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    closeForm() {
      closeAllForms();
      // Deactivate backdrop
      const backdrop = document.getElementById('backdrop');
      if (backdrop) {
        backdrop.style.display = 'none';
      }
      document.body.classList.remove('slideform-open'); // This is to prevent the background from scrolling when the form is open
    },
    validateForm(event) {
      const form = event.target.closest('form');

      // Ensure the start and end dates are defined
      if (!this.startDate || !this.endDate) {
        event.preventDefault();
        alert('Please select a valid start and end date.');
        return;
      }

      if (form.checkValidity()) {
        this.submitForm(event);
      } else {
        form.reportValidity();
      }
    },
    submitForm(event) {
      console.log('FORM SUBMITTED!!');
      this.closeForm();
      const calendarStore = Alpine.store('calendarStore');
      const toastStore = Alpine.store('toastStore');
      calendarStore.isLoading = true;
      const formElement = event.target.form || event.currentTarget.form;
      const formData = new FormData(formElement);
      if (
        this.carriedUnitIds.length > 1 &&
        Number(this.units) === this.carriedUnitIds.length
      ) {
        this.carriedUnitIds.forEach((unitId) => {
          formData.append('selected_unit_ids', unitId);
        });
      }

      console.log('formData:', formData);

      fetch('/bookings_app/create_block/', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRFToken': getCSRFToken(),
        },
      })
        .then(async (response) => {
          const data = await safeJsonParse(response);
          return { status: response.status, body: data };
        })
        .then(({ status, body }) => {
          if (status >= 400) throw body;
          console.log('Block created successfully:', body);

          // Extract all new block items from the response
          const newBlockItems = body.blockItem; // Get all block items from the array
          console.log('newBlockItems:', newBlockItems);
          // Add all new block items to the existing block items
          const updatedBlockItems = [
            ...calendarStore.blockItems,
            ...newBlockItems,
          ];

          // Update the store with the new list of block items
          calendarStore.blockItems = updatedBlockItems;
          calendarStore.syncBookedDatesFromItems();
          // Clear filter cache to ensure new items are rendered
          calendarStore._bookingItemsCache = null;
          calendarStore._blockItemsCache = null;

          console.log(
            'Updated calendarStore.blockItems:',
            calendarStore.blockItems,
          );

          calendarStore.isLoading = false;
          calendarStore.drainPendingItemsRefresh();
          toastStore.addToast(body.message, 'success');

          // Clear fields only in the block form
          const blockFormId =
            this.product === 'Accommodation'
              ? 'addAccommodationBlockForm'
              : 'addTourBlockForm';
          const blockForm = document.getElementById(blockFormId);
          if (blockForm) {
            const fields = blockForm.getElementsByClassName('slideForm-field');
            Array.from(fields).forEach((field) => {
              field.value = '';
            });
          }
        })
        .catch((error) => {
          console.error('Error creating block:', error);
          calendarStore.isLoading = false;
          toastStore.addToast(
            error.message || 'An unexpected error occurred',
            'danger',
          );
        });
    },
  });
}
